import API from 'shared/constants/api';
import types from 'shared/constants/json/transportTypes.json';
import DESCRIPTION from 'shared/constants/seoDescriptions';
import TITLE from 'shared/constants/seoTitles';
import { bodyCategories } from 'shared/routes/search/categoriesSearch';
import uniq from 'lodash/uniq';
import {
  setFormVocabulary,
  selectedActions,
  carFormFillThunks,
  motoFormFillThunks,
  commercialFormFillThunks,
  carSetThunks,
  resultsThunks,
  resetAll,
} from 'shared/actions/searchForm';
import { getResultFromStoreByKey } from 'shared/helpers/common';
import LINKS from 'desktop/constants/navigation';
import priceStatuses from 'shared/constants/json/priceStatuses.json';
import snakeCaseKeys from 'snakecase-keys';
import queryString from 'query-string';
import { getAdsContext, getPriceValuation } from '../../helpers/adsAndMetrics';
import { getRootTransportType } from '../../helpers/transportTypes';

export const PAGE_TYPES = {
  OFFERS: 'OFFERS',
  NEWS: 'NEWS',
  ABOUT: 'ABOUT',
};

const getCanonicalList = async (id, fetch) =>
  fetch(API.DEALER.MAKES_COUNT(id))
    .then(res => res.json())
    .then(response => (response.err ? null : response));

export default async function(
  { fetch, store, baseUrl, path, query, params, translate },
  pageType = PAGE_TYPES.OFFERS,
) {
  const url = `${baseUrl}${path}`.replace('//', '/');
  const { slug, id } = params;
  const state = store.getState();
  const { locale, dealerSlug } = state.globals;

  const storeDealer = state && state.searchForm.selected.dealer;
  const storeTransport = state && state.searchForm.selected.transport;
  const isInStore = storeDealer && storeDealer.slug === slug;
  const dealer = isInStore
    ? storeDealer
    : await fetch(API.DEALERS.ITEM(slug)).then(r => {
        if (r.status === 404)
          return {
            status: 404,
            redirect: '/404/',
          };
        return r && r.json();
      });

  if (!dealer || !dealer.id) {
    return null;
  }
  let body = {};

  const dealersTypesIds = uniq((dealer.makes || []).map(make => make.transport));
  const dealersTypes = uniq(
    types.filter(type => dealersTypesIds.includes(type.id)).map(type => {
      if (type && LINKS.SEARCH[type.slug]) {
        return type;
      } else if (type.parents && type.parents.length > 0) {
        return types.find(item => item.id === type.parents[0]);
      }

      return type;
    }),
  );
  const { page, transport } = query;

  const currentPage = page ? parseFloat(page) : 1;

  let transportType = types.find(item => item.slug === 'avto');

  let transportID;
  if (storeTransport && dealersTypes.find(type => type.id === storeTransport.id)) {
    transportID = storeTransport.id;
  } else if (
    transport &&
    dealersTypes.find(type => parseFloat(type.id) === parseFloat(transport))
  ) {
    transportID = parseFloat(transport);
  }
  if (storeTransport && transport && parseFloat(transport) !== storeTransport.id) {
    transportID = parseFloat(transport);
  }

  if (transportID) {
    transportType = types.find(item => item.id === transportID);
  } else if (dealersTypes && dealersTypes.length > 0) {
    transportType = dealersTypes && dealersTypes[0];
    transportID = transportType && transportType.id;
  }

  const prevSelectedTransport = state.searchForm.selected && state.searchForm.selected.transport;
  if (prevSelectedTransport && prevSelectedTransport.id !== transportID) {
    await store.dispatch(resetAll());
  }
  await store.dispatch(selectedActions.set.transport(transportType));

  const currency = state.searchForm.selected && state.searchForm.selected.currency;
  const q = { ...query };
  delete q.tab;
  q.dealer = dealer.id;
  q.transport = transportID;
  q.currency = currency.slug;
  q.isCrashed = q.isNew ? null : q.isCrashed;
  if (q.isNew === undefined) {
    q.isNew = null;
  }

  const carsListTypes = {
    result: queryString.stringify(snakeCaseKeys({ ...q, page: currentPage })),
  };

  const rootTransportType = getRootTransportType(transportType);
  const isMotoSearch = rootTransportType && rootTransportType.slug === 'moto';
  const isCommercial =
    transportType.slug === 'avtobus' ||
    transportType.slug === 'pritsepy' ||
    transportType.slug === 'spetstehnika' ||
    transportType.slug === 'gruzovye';

  let formFillThunks;
  if (isMotoSearch) {
    formFillThunks = motoFormFillThunks;
  } else if (isCommercial) {
    formFillThunks = commercialFormFillThunks;
  } else {
    formFillThunks = carFormFillThunks;
  }

  Object.keys(q).forEach(key => {
    const item = q[key];
    if (formFillThunks[key]) {
      store.dispatch(formFillThunks[key](item));
    }
  });

  const vocabulary = state.searchForm.vocabulary;
  const missingVocabularyFields =
    Object.keys(vocabulary).filter(key => !vocabulary[key] || key !== 'makes') || [];

  const fetchedVocabulary = (await Promise.all(
    missingVocabularyFields.map(
      field =>
        API.VOCABULARY[field]
          ? fetch(API.VOCABULARY[field](transportID))
              .then(r => r.json())
              .then(response => ({ [field]: response }))
          : Promise.resolve({}),
    ),
  )).reduce((result, pair) => ({ ...result, ...pair }), {});

  fetchedVocabulary.makes = (dealer.makes || []).filter(m => m.transport === transportID);

  await store.dispatch(
    setFormVocabulary({
      ...fetchedVocabulary,
      priceStatuses,
    }),
  );
  const { searchForm: { vocabulary: { categories } } } = store.getState();
  for (let a = 0; a < categories.length; a += 1) {
    const buf = categories[a] && categories[a].title && categories[a].id;
    if (buf && query.category && String(buf) === query.category) {
      body = categories[a];
      break;
    }
  }
  let title;
  let description;
  switch (pageType) {
    case PAGE_TYPES.NEWS: {
      title = TITLE.DEALER_PAGE.NEWS(dealer.title, translate);
      description = DESCRIPTION.DEALER_PAGE.NEWS(dealer.title, translate);
      break;
    }
    case PAGE_TYPES.ABOUT: {
      title = TITLE.DEALER_PAGE.ABOUT(dealer.title, translate);
      description = DESCRIPTION.DEALER_PAGE.ABOUT(dealer.title, translate);
      break;
    }
    default: {
      title = TITLE.DEALER_PAGE.ADVERTS(dealer.title, translate);
      description = DESCRIPTION.DEALER_PAGE.ADVERTS(
        {
          dealer: dealer.title,
          isOfficial: dealer.is_official,
          makes: dealer.makes || [],
        },
        translate,
      );
    }
  }

  await store.dispatch(carSetThunks.count());
  const [carsResult] = await store.dispatch(resultsThunks.searchResults(carsListTypes));

  const firmAboutArticleResponse =
    pageType === PAGE_TYPES.ABOUT
      ? await fetch(API.JOURNAL.LIST({ agency: dealer.id, rubric: 'home-page' })).then(
          r => (r.ok ? r.json() : {}),
        )
      : {};
  const aboutArticle =
    firmAboutArticleResponse.count > 0 ? firmAboutArticleResponse.results[0] : {};

  const result = getResultFromStoreByKey(store, carsListTypes.result);
  const isLastPage = !(result && result.next);

  const location = store.getState().globals.location;
  const seo = {
    ...location,
    ...dealer,
  };
  const { searchForm: { results: { result: { results = [] } = {} } = {} } } = store.getState();
  if (!body.slug && q.make && q.model) {
    body.slug = bodyCategories(results, categories);
  }
  const targetingArguments = getAdsContext({
    make: seo.make,
    model: seo.model,
    isMain: seo.isMain,
    isNew: seo.isNew,
    rubric: seo.rubric,
    isCustomCleared: seo.isCustomCleared,
    location: seo.location,
    isCountry: seo.isCountry,
    transport: transportType,
    category: body,
    engine: seo.engine,
    dealer: seo.rubric,
    price: getPriceValuation({ from: q.priceFrom, to: q.priceTo }, q.currency),
    page: seo.page,
    id: seo.slug,
  });

  const canonicalList = await getCanonicalList(dealer.id, fetch);

  return {
    url,
    transportType,
    dealer: {
      ...dealer,
      description: aboutArticle.content || dealer.description,
    },
    carsListTypes,
    currentPage,
    dealersTypes,
    locale,
    title,
    description,
    query,
    isLastPage,
    isMotoSearch,
    isCommercial,
    targetingArguments,
    slug,
    dealerSlug,
    canonicalList,
    carsResult,
    // newsInfo,
  };
}



// WEBPACK FOOTER //
// ./src/shared/routes/dealer/index.js